import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';

import RELEASE_NOTES from 'services/releaseNotes';
import translations from 'services/translations/translations';

import 'style/layout/ReleaseNotes.css';

// The version the user has last read the notes of, in this browser
const SEEN_KEY = 'release-notes-seen';

const readSeen = (): null | string => {
  try {
    return window.localStorage.getItem(SEEN_KEY);
  } catch (error) {
    return null;
  }
};

const formatDay = (day: string, language: string) =>
  new Date(`${day}T12:00:00`).toLocaleDateString(language);

/**
 * The i of the top bar: what each version brought, the new features and what was fixed, as
 * headlines. A dot on the button tells of a version whose notes the user has not opened.
 */
const ReleaseNotesButton = (): JSX.Element | null => {
  const { t, i18n } = useTranslation([translations.common]);
  const tr = (key: string) => t(`${translations.common}:releaseNotes.${key}`);
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState(readSeen);

  const latest = RELEASE_NOTES[0];
  if (latest === undefined) {
    return null;
  }

  const unread = seen !== latest.date;

  const openNotes = () => {
    setOpen(true);
    setSeen(latest.date);
    try {
      window.localStorage.setItem(SEEN_KEY, latest.date);
    } catch (error) {
      // Only remembered for the user's convenience
    }
  };

  return (
    <>
      <button
        aria-label={tr('Title')}
        className="release-notes-button"
        onClick={openNotes}
        title={tr('Title')}
        type="button"
      >
        i{unread ? <span className="release-notes-dot" /> : null}
      </button>
      <Modal
        className="release-notes-modal"
        isOpen={open}
        scrollable
        toggle={() => setOpen(false)}
      >
        <ModalHeader toggle={() => setOpen(false)}>{tr('Title')}</ModalHeader>
        <ModalBody>
          {RELEASE_NOTES.map((note, index) => (
            <section className="release-notes-version" key={note.date}>
              <h3>
                {formatDay(note.date, i18n.language)}
                {index === 0 ? (
                  <span className="release-notes-current">{tr('Current')}</span>
                ) : null}
              </h3>
              {note.new.length > 0 ? (
                <>
                  <h4>{tr('New')}</h4>
                  <ul className="release-notes-new">
                    {note.new.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </>
              ) : null}
              {note.fixed.length > 0 ? (
                <>
                  <h4>{tr('Fixed')}</h4>
                  <ul className="release-notes-fixed">
                    {note.fixed.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </>
              ) : null}
            </section>
          ))}
        </ModalBody>
      </Modal>
    </>
  );
};

export default ReleaseNotesButton;
